import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { EnvironmentService } from '../../../core/environment/environment.service';
import { Menu } from '../../menu/menu.model';
import { MenuSubMenu } from '../../menu/menu-sub-menu.model';
import { LanguageService } from '../../../core/language/language.service';
import { SdkAnalyticsService } from '@app/shared/services/sdk-analytics/sdk-analytics.service';
import { PermissionService } from '../../../core/permissions/permission.service';
import { normalizePath, sanitizeDetail, sanitizeLabel } from '../../utils';

const serviceUrl: string = '/menu';
const searchResultsUrl: string = '/busca';

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  private menuList: Array<Menu>;
  menuSubject: BehaviorSubject<Array<Menu>> = new BehaviorSubject<Array<Menu>>(this.menuList);
  apiUrl: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private environmentService: EnvironmentService,
    private languageService: LanguageService,
    private _sdkAnalyticsService: SdkAnalyticsService,
    private permissionService: PermissionService
  ) {
    this.apiUrl = this.environmentService.envModel.appSettings.apiUrlMenu;
  }

  getMenu(): Observable<Array<Menu>> {
    return this.menuSubject.asObservable();
  }

  /**
   * Busca menu do endpoint EN4 e inicializa cache de permissoes
   * O cache e criado na primeira chamada bem-sucedida ao endpoint /menu
   */
  fetchMenu(language: string, jwt: string): void {
    const headers = new HttpHeaders({ jwt });

    this.http.get<Array<Menu>>(
      this.getServiceUrl(language, jwt), { headers, withCredentials: true })
      .subscribe((res: Array<Menu>) => {
        this.updateMenuList(res);
        // Salva permissoes no cache apos primeira chamada bem-sucedida ao endpoint /menu
        this.savePermissionsToCache(res);
      });
  }

  /**
   * Salva permissoes no cache usando PermissionService
   * Estrutura: { menus, timestamp, userid }
   */
  private savePermissionsToCache(menus: Array<Menu>): void {
    const now = Date.now();
    const permissionTree = {
      menus: menus,
      timestamp: now,
      expiresAt: now + 30 * 60 * 1000, // 30 minutos
      userId: '' // Sera preenchido pelo PermissionService com sanitizeOperatorId
    };
    this.permissionService.savePermissionsToStorage(permissionTree);
  }

  fullMenu(language: string, menuName: string, menuCategory?: string, date?: number): void {
    if(menuName && this.router && this.router.url){
      this._sdkAnalyticsService.trackEvent( 'click', {
        custom_path: normalizePath(this.router.url),
        implementation_team: "output-de-dados-do-ativo-monitores-de-status:experiencia-do-cliente",
        detail: menuCategory ? "menu-"+sanitizeLabel(menuName)+":"+sanitizeLabel(menuCategory) : "menu-"+sanitizeLabel(menuName)
      });
    }
    window.scrollTo(0, 0);
    if (menuCategory)
      this.router.navigate([serviceUrl, { 'menuName': menuName, 'category': menuCategory, 'r': date }]);
    else
      this.router.navigate([serviceUrl, { 'menuName': menuName }]);

    this.scrollIntoCategory(menuCategory);
  }

  openContext(route: string): void {
    this.setTimeoutNavigation(route, 3);
  }

  setTimeoutNavigation(route: string, retries: number): void {
    let attempts = 0;
    const intervalId = setInterval(() => {
      this.router.navigate([route]).then(success => {
        if (success) {
          clearInterval(intervalId);
        } else {
          attempts++;
          if (attempts >= retries) {
            clearInterval(intervalId);
          }
        }
      }).catch(error => {
        attempts++;
        if (attempts >= retries) {
          console.log(error)
          clearInterval(intervalId);
        }
      });
    }, 500);
  }

  getPath(): void {
    this.menuList.forEach(
      menu => {
        if (menu.categories != null) {
          menu.categories.forEach(subCategory => {
            if (subCategory.subMenus != null) {
              subCategory.subMenus.forEach(sub => {
                sub.path = this.languageService.translate(menu.translationKey) + " > " + this.languageService.translate(subCategory.translationKey) + (sub.subCategory == null ? " > " : " > " + this.languageService.translate(sub.subCategory) + " > ") + this.languageService.translate(sub.translationKey);
              });
            }
          });
        }
      }
    );
  }

  searchFor(searchTerm: string) {
    window.scrollTo(0, 0);
    let url: string = this.router.url;
    this.router.navigate([searchResultsUrl, { 'searchTerm': searchTerm }]);
  }

  filterMenu(value: string): Array<MenuSubMenu> {

    if (!value) {
      return null;
    }
    const filterValue = value.toLowerCase();

    var result: Array<MenuSubMenu> = new Array<MenuSubMenu>();
    var resultSubMenu: Array<MenuSubMenu>;

    resultSubMenu = this.filterSubMenus(filterValue, "name");
    resultSubMenu = resultSubMenu.concat(this.filterSubMenus(filterValue, "path"));
    resultSubMenu = resultSubMenu.filter(function (elem, index, self) { return index === self.indexOf(elem); }); //Removes duplicates entries
    [...resultSubMenu].sort((a, b) => a.name.localeCompare(b.name)).forEach(x => {
      result.push(x);
    });

    return result;
  }

  private filterSubMenus(filterValue: string, filterField: string): Array<MenuSubMenu> {
    let menus = this.menuList;
    let result: Array<MenuSubMenu> = new Array<MenuSubMenu>();

    for (let i = 0; i < menus.length; i++) {
      const menu = menus[i];
      if (menu.categories != null) {
        for (let x = 0; x < menu.categories.length; x++) {
          const category = menu.categories[x];
          if (category.subMenus != null) {
            for (let z = 0; z < category.subMenus.length; z++) {
              const subMenu = category.subMenus[z];
              const field: string = (filterField == "name") ? this.languageService.translate(subMenu.translationKey) : subMenu.path;

              if (field.toLowerCase().includes(filterValue)) {
                result.push(subMenu);
              }
            }
          }
        }
      }

    }

    return result;
  }

  private updateMenuList(newMenu: Array<Menu>) {
    this.menuList = newMenu;
    this.getPath();
    this.menuSubject.next(this.menuList);
    return this.menuList;
  }

  private getServiceUrl(language: string, jwt: string) {
    return this.apiUrl + serviceUrl;
  }

  private scrollIntoCategory(category) {

    let element: HTMLElement;
    let header = 0;
    if (document.getElementById("header") != null && document.getElementById("header").offsetHeight != null)
      header = document.getElementById("header").offsetHeight;
    if (document.getElementById('title_' + category) != null)
      element = document.getElementById('title_' + category)
    if (element) {
      let scrollElm: Element = document.scrollingElement;
      scrollElm.scrollTop = 0;
      setTimeout(function () { scrollElm.scrollTop = element.offsetTop - header; }, 100);

    }
  }
}
